import React, { lazy, Suspense } from 'react';
import { Box, Typography, Button, Grid, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useInView, usePrefersReducedMotion } from './animations/AnimationUtils';

// Lazy load feature cards for performance
const IdeaGenCard = lazy(() => import('./cards/IdeaGenCard'));
const BrandPulseCard = lazy(() => import('./cards/BrandPulseCard'));
const ImageGuardCard = lazy(() => import('./cards/ImageGuardCard'));
const PlatformFitCard = lazy(() => import('./cards/ChannelFitCard')); // Renamed to PlatformFitCard
const PersonaLensCard = lazy(() => import('./cards/PersonaLensCard'));
const ReplyExplorerCard = lazy(() => import('./cards/ReplyExplorerCard'));

const CardSkeleton: React.FC = () => (
  <Box
    sx={{
      background: 'rgba(15, 51, 89, 0.75)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      p: 3,
      height: 320,
    }}
  >
    <Skeleton variant="rectangular" width={60} height={20} sx={{ mb: 3 }} />
    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
    <Skeleton variant="text" width="100%" height={16} sx={{ mb: 3 }} />
    <Skeleton variant="rectangular" width="100%" height={120} />
  </Box>
);

const FeaturesSection: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const prefersReducedMotion = usePrefersReducedMotion();

  const handlePrimaryCTA = () => {
    // Track analytics
    if (window.gtag) {
      window.gtag('event', 'features_primary_cta', {
        event_category: 'Features',
        event_label: 'Try Idea Generation Free',
      });
    }
    navigate('/login?source=features-ideagen');
  };

  const handleSecondaryCTA = () => {
    // Track analytics
    if (window.gtag) {
      window.gtag('event', 'features_secondary_cta', {
        event_category: 'Features',
        event_label: 'Run a Quick Simulation',
      });
    }
    navigate('/login?source=features-simulation');
  };

  // Determine grid columns based on screen size
  const getGridColumns = () => {
    if (isMobile) return 12; // 1 column
    if (isTablet) return 6; // 2 columns
    return 4; // 3 columns
  };

  const gridColumns = getGridColumns();

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        maxWidth: 1400,
        mx: 'auto',
      }}
    >
      {/* Section Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
            mb: 2,
            color: 'white',
            opacity: isSectionInView || prefersReducedMotion ? 1 : 0,
            transform: isSectionInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
            transition: prefersReducedMotion ? 'none' : 'all 600ms ease-out',
          }}
        >
          Less Guesswork. More Green Lights.
        </Typography>
        <Typography
          variant="h5"
          sx={{
            maxWidth: 800,
            mx: 'auto',
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: { xs: '1rem', md: '1.15rem' },
            lineHeight: 1.6,
            opacity: isSectionInView || prefersReducedMotion ? 1 : 0,
            transform: isSectionInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
            transition: prefersReducedMotion ? 'none' : 'all 600ms ease-out 200ms',
          }}
        >
          Generate on-brand ideas, predict reactions, and de-risk creative before you publish across X, Instagram, LinkedIn, and TikTok.
        </Typography>
      </Box>

      {/* Feature Cards Grid */}
      <Grid container spacing={3} sx={{ mb: { xs: 6, md: 8 } }}>
        <Grid item xs={12} sm={gridColumns}>
          <Suspense fallback={<CardSkeleton />}>
            <IdeaGenCard index={0} />
          </Suspense>
        </Grid>
        <Grid item xs={12} sm={gridColumns}>
          <Suspense fallback={<CardSkeleton />}>
            <BrandPulseCard index={1} />
          </Suspense>
        </Grid>
        <Grid item xs={12} sm={gridColumns}>
          <Suspense fallback={<CardSkeleton />}>
            <ImageGuardCard index={2} />
          </Suspense>
        </Grid>
        <Grid item xs={12} sm={gridColumns}>
          <Suspense fallback={<CardSkeleton />}>
            <PlatformFitCard index={3} />
          </Suspense>
        </Grid>
        <Grid item xs={12} sm={gridColumns}>
          <Suspense fallback={<CardSkeleton />}>
            <PersonaLensCard index={4} />
          </Suspense>
        </Grid>
        <Grid item xs={12} sm={gridColumns}>
          <Suspense fallback={<CardSkeleton />}>
            <ReplyExplorerCard index={5} />
          </Suspense>
        </Grid>
      </Grid>

      {/* CTA Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'center',
          alignItems: 'center',
          opacity: isSectionInView || prefersReducedMotion ? 1 : 0,
          transform: isSectionInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
          transition: prefersReducedMotion ? 'none' : 'all 600ms ease-out 400ms',
        }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={handlePrimaryCTA}
          sx={{
            borderRadius: 28,
            px: 4,
            py: 1.5,
            fontSize: { xs: '0.95rem', md: '1rem' },
            fontWeight: 600,
            backgroundColor: '#0a7ca0',
            color: 'white',
            minWidth: { xs: '100%', sm: 200 },
            '&:hover': {
              backgroundColor: '#005173',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(10, 124, 160, 0.3)',
            },
          }}
        >
          Try Idea Generation Free
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={handleSecondaryCTA}
          sx={{
            borderRadius: 28,
            px: 4,
            py: 1.5,
            fontSize: { xs: '0.95rem', md: '1rem' },
            fontWeight: 600,
            borderColor: 'rgba(10, 124, 160, 0.5)',
            color: '#0a7ca0',
            minWidth: { xs: '100%', sm: 200 },
            '&:hover': {
              borderColor: '#0a7ca0',
              backgroundColor: 'rgba(10, 124, 160, 0.1)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          Run a Quick Simulation
        </Button>
      </Box>
    </Box>
  );
};

export default FeaturesSection;